import { useAuthenticatedAxiosInstance } from './useAuthenticatedAxiosInstance';
import { useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { getEnvironmentConfig } from '../environmentConfig';
import type { FormElement } from '@glueapp/graph-types';

export interface Form {
    id: string;
    publicUrl: string;
    nodeInstanceId: number;
    title: string;
    description: string | null;
    fields: FormElement[];
    successMessage: string | null;
    redirectUrl: string | null;
    isPrivate: boolean;
    createdAt: string;
    updatedAt: string;
}

export const useFormApi = () => {
    const api = useAuthenticatedAxiosInstance(`${getEnvironmentConfig().graphServiceUrl}`);

    const getFormsForGraph = useCallback(
        async (graphId: string): Promise<Form[]> => {
            const result: AxiosResponse = await api.get(`/forms/graph/${graphId}`);
            return result.data.forms;
        },
        [api],
    );

    const createForm = useCallback(
        async (graphId: string, nodeInstanceId: number, title: string): Promise<Form> => {
            const result: AxiosResponse = await api.post(`/forms`, { graphId, nodeInstanceId, title });
            return result.data.form;
        },
        [api],
    );

    const updateForm = useCallback(
        async (
            formId: string,
            updates: {
                title?: string;
                description?: string | null;
                fields?: FormElement[];
                successMessage?: string | null;
                redirectUrl?: string | null;
                password?: string | null;
                submitLabel?: string | null;
            },
        ): Promise<Form> => {
            const result: AxiosResponse = await api.put(`/forms/${formId}`, updates);
            return result.data.form;
        },
        [api],
    );

    const deleteForm = useCallback(
        async (formId: string): Promise<void> => {
            await api.delete(`/forms/${formId}`);
        },
        [api],
    );

    return {
        getFormsForGraph,
        createForm,
        updateForm,
        deleteForm,
    };
};
